import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  createTrackedFeature,
  deleteTrackedFeature,
  getDashboardFeatureUsage,
  getFeatureCatalog,
  getUserFeatureUsage,
  listTrackedFeatures,
  updateTrackedFeature,
} from './api';
import type {
  FeatureKind,
  FeatureUsageParams,
  TrackedFeatureFilter,
  UpdateTrackedFeatureInput,
} from './types';

export const featureTrackingKeys = {
  all: ['admin', 'feature-tracking'] as const,
  dashboard: (params: FeatureUsageParams) =>
    [...featureTrackingKeys.all, 'dashboard', params] as const,
  user: (publicId: string | undefined, params: FeatureUsageParams) =>
    [...featureTrackingKeys.all, 'user', publicId, params] as const,
  list: (filter: TrackedFeatureFilter) => [...featureTrackingKeys.all, 'list', filter] as const,
  catalog: (kind: FeatureKind | undefined, q: string) =>
    [...featureTrackingKeys.all, 'catalog', kind, q] as const,
};

const STALE_MS = 30_000;

export const useAdminFeatureUsage = (params: FeatureUsageParams, enabled = true) =>
  useQuery({
    queryKey: featureTrackingKeys.dashboard(params),
    queryFn: () => getDashboardFeatureUsage(params),
    enabled,
    staleTime: STALE_MS,
    placeholderData: keepPreviousData,
  });

export const useUserFeatureUsage = (publicId: string | undefined, params: FeatureUsageParams) =>
  useQuery({
    queryKey: featureTrackingKeys.user(publicId, params),
    queryFn: () => getUserFeatureUsage(publicId as string, params),
    enabled: Boolean(publicId),
    staleTime: STALE_MS,
    placeholderData: keepPreviousData,
  });

export const useTrackedFeatures = (filter: TrackedFeatureFilter) =>
  useQuery({
    queryKey: featureTrackingKeys.list(filter),
    queryFn: () => listTrackedFeatures(filter),
    placeholderData: keepPreviousData,
  });

export const useFeatureCatalog = (kind: FeatureKind | undefined, q: string, enabled: boolean) =>
  useQuery({
    queryKey: featureTrackingKeys.catalog(kind, q),
    queryFn: () => getFeatureCatalog({ kind, q: q || undefined, limit: 50 }),
    enabled,
  });

/** Config changes alter both the table and the charts, so every feature-tracking query refetches. */
const useInvalidateFeatureTracking = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: featureTrackingKeys.all });
};

export const useCreateTrackedFeature = () => {
  const invalidate = useInvalidateFeatureTracking();
  return useMutation({ mutationFn: createTrackedFeature, onSuccess: invalidate });
};

export const useUpdateTrackedFeature = () => {
  const invalidate = useInvalidateFeatureTracking();
  return useMutation({
    mutationFn: (vars: { publicId: string; input: UpdateTrackedFeatureInput }) =>
      updateTrackedFeature(vars.publicId, vars.input),
    onSuccess: invalidate,
  });
};

export const useDeleteTrackedFeature = () => {
  const invalidate = useInvalidateFeatureTracking();
  return useMutation({ mutationFn: deleteTrackedFeature, onSuccess: invalidate });
};
